import React, { ChangeEvent, useState } from 'react';

import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityOffRoundedIcon from '@mui/icons-material/VisibilityOffRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import { Button, Card, CardActions, CardContent, Stack, TextField, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';

import Image from '../Image';

interface Props {
  id: string | number;
  publicImageId: string;
  order: number;
  title?: string;
  description?: string;
  hidden?: boolean;
  filename?: string;
  is_hero?: boolean;
  source?: string;
  onChangeTitle: (imageId: string | number, newTitle: string) => any;
  onChangeDescription: (imageId: string | number, newDescription: string) => any;
  onDeleteImage: (imageId: string | number) => void;
  onHideImage: (imageId: string | number) => () => void;
  hasHiddenToggle?: boolean;
  hideDeleteButton?: boolean;
}

const imageOptions = {
  width: 400,
  height: 300,
};

const HighlightPanel = (props: Props) => {
  const {
    id,
    publicImageId,
    order,
    title,
    description,
    hidden,
    filename,
    onChangeTitle,
    onChangeDescription,
    onDeleteImage,
    onHideImage,
    hasHiddenToggle = false,
    hideDeleteButton = false,
  } = props;

  const [localTitle, setLocalTitle] = useState(title || '');
  const [localDescription, setLocalDescription] = useState(description || '');

  const { attributes, isDragging, listeners, setNodeRef, transform, transition } = useSortable({ id });

  const updateTitle = (e: ChangeEvent<HTMLInputElement>) => {
    setLocalTitle(e.target.value);
    onChangeTitle(id, e.target.value);
  };

  const updateDescription = (e: ChangeEvent<HTMLInputElement>) => {
    setLocalDescription(e.target.value);
    onChangeDescription(id, e.target.value);
  };

  return (
    <Grid size={4}>
      <Card
        {...listeners}
        {...attributes}
        raised={isDragging}
        ref={setNodeRef}
        sx={{
          flexGrow: 1,
          transform: CSS.Transform.toString(transform),
          transition,
          cursor: isDragging ? 'grabbing' : 'grab',
        }}
        variant="outlined"
      >
        <CardContent>
          <Image
            className="img-responsive mx-auto"
            publicId={publicImageId}
            options={imageOptions}
            style={
              hidden
                ? {
                    filter: 'grayscale(1)',
                    opacity: 0.75,
                  }
                : {}
            }
          />
        </CardContent>
        <CardActions>
          <Stack sx={{ width: '100%' }} spacing={2}>
            <Typography variant="subtitle2">{filename}</Typography>
            <Stack direction="row">
              <Typography variant="h5" pr={2}>
                {order}
              </Typography>
              <TextField
                fullWidth
                variant="standard"
                id={`title-${id}`}
                name="title"
                type="text"
                className="image-title-input undraggable"
                value={localTitle}
                onChange={updateTitle}
                placeholder="A title for the highlight"
              />
            </Stack>
            <Stack>
              <TextField
                fullWidth
                variant="standard"
                id={`description-${id}`}
                name="description"
                type="text"
                className="image-description-input undraggable"
                value={localDescription}
                onChange={updateDescription}
                placeholder="A short description of the highlight"
              />
            </Stack>
            <Stack justifyContent="space-between" alignItems="center" direction="row">
              {hasHiddenToggle && (
                <Button
                  variant="contained"
                  startIcon={hidden ? <VisibilityRoundedIcon /> : <VisibilityOffRoundedIcon />}
                  color={hidden ? 'success' : 'info'}
                  onClick={onHideImage(id)}
                >
                  <Typography variant="subtitle2">
                    &nbsp;{hidden ? 'Show' : 'Hide'}
                    &nbsp;Image
                  </Typography>
                </Button>
              )}
              {hideDeleteButton || (
                <Button variant="contained" color="error" onClick={() => onDeleteImage(id)} startIcon={<DeleteIcon />}>
                  <Typography noWrap variant="subtitle2">
                    Delete image
                  </Typography>
                </Button>
              )}
            </Stack>
          </Stack>
        </CardActions>
      </Card>
    </Grid>
  );
};

export default HighlightPanel;
